import { NewButton } from '@/components/basic';
import { Theme } from '@/components/basic/NewButton';
import { VirtualizedSearchableList } from '@/components/partial/SearchableList/VirtualizedSearchableList';
import { UserListItem } from '@/components/partial/TeamSettingsPanel/UserListItem';
import { api } from '@/lib/api/api';
import { classNames } from '@/lib/classNames';
import { IUserFull } from '@gettactic/api';
import { useUserList } from '@gettactic/hooks';
import { Switch } from '@headlessui/react';
import { useEffect, useMemo, useState } from 'react';

const globalUserFetchInProgress: { [id: string]: boolean } = {};

function useUserListAndRequired(userIds: string[], query: string) {
  const userList = useUserList(query);
  const [missing, setMissing] = useState<{ [id: string]: IUserFull }>({});
  useEffect(() => {
    if (!userList.isSuccess) {
      return;
    }
    const elements = userList.data?.elements ?? [];
    const lookup = userIds.map(async (id) => {
      if (
        elements.find((x) => x.id === id) ||
        missing[id] ||
        globalUserFetchInProgress[id]
      ) {
        return Promise.resolve();
      }
      // we start setting the global dict on true, so we avoid multiple fetch of the same res
      globalUserFetchInProgress[id] = true;
      const user = await api.client.organizations.user(id, true);
      delete globalUserFetchInProgress[id];
      if (user.user) {
        setMissing((x) => ({ ...x, [id]: user.user as IUserFull }));
      }
    });
    Promise.all(lookup).then();
  }, [userList, userIds, userList.isLoading]);

  const list = useMemo(() => {
    const fetched = userList.data?.elements ?? [];
    const notFound = userIds.filter((id) => !fetched.find((x) => x.id === id));
    const foundInMissing = notFound
      .map((x) => missing[x])
      .filter((user) => !!user);
    return fetched.concat(foundInMissing);
  }, [userList, missing, userIds, userList.isLoading]);

  return { list };
}

type Props = {
  ids: string[];
  add: (id: string) => void;
  remove: (id: string) => void;
  isAllUsers?: boolean;
};
export default function UsersPermissionList({
  ids,
  add,
  remove,
  isAllUsers
}: Props) {
  const [currentOver, setCurrentOver] = useState('');
  const [query, setQuery] = useState('');
  const [displayOnlyAssigned, setDisplayOnlyAssigned] = useState(false);
  const { list: listFormIds } = useUserListAndRequired(
    ids.filter((id) => id !== '*'),
    query
  );
  const list: Pick<IUserFull, 'id' | 'name'>[] = isAllUsers
    ? [
        {
          id: '*',
          name: 'All users'
        }
      ]
    : listFormIds;

  return (
    <VirtualizedSearchableList
      teamSearchQuery={query}
      setSearchQuery={setQuery}
      scrollAreaClassName={isAllUsers ? 'h-auto' : undefined}
      offsetScrollbars={!isAllUsers}
      className="pb-0"
      maxRenderedItems={5}
      actionElement={
        <Switch.Group>
          <div className="flex items-center gap-3">
            <Switch.Label className="text-base font-medium text-gray-900">
              All
            </Switch.Label>
            <Switch
              checked={displayOnlyAssigned}
              onChange={() => setDisplayOnlyAssigned((x) => !x)}
              className={`${displayOnlyAssigned ? 'bg-primary' : 'bg-disabled'} relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out \
              focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2`}
            >
              <span
                className={classNames(
                  displayOnlyAssigned ? 'translate-x-5' : 'translate-x-0',
                  'pointer-events-none relative inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
                )}
              />
            </Switch>
            <Switch.Label className="text-base font-medium text-gray-900">
              Can Reserve
            </Switch.Label>
          </div>
        </Switch.Group>
      }
    >
      {list
        ? list.map((user) => {
            const alreadyIn = ids.indexOf(user.id) > -1;
            if (
              (displayOnlyAssigned && !alreadyIn) ||
              (query &&
                user.name.toLowerCase().indexOf(query.toLowerCase()) === -1)
            ) {
              return null;
            }
            return (
              <UserListItem
                key={user.id}
                user={user}
                rightElement={
                  <NewButton.Component
                    variant={NewButton.Variant.PrimaryShort}
                    size={NewButton.Size.Medium}
                    theme={alreadyIn ? Theme.Primary : Theme.Base}
                    onMouseOver={() => setCurrentOver(user.id)}
                    onMouseLeave={() => setCurrentOver('')}
                    onClick={() => (alreadyIn ? remove(user.id) : add(user.id))}
                  >
                    {alreadyIn
                      ? currentOver === user.id
                        ? 'Remove'
                        : 'Can Reserve'
                      : 'Add'}
                  </NewButton.Component>
                }
              />
            );
          })
        : null}
    </VirtualizedSearchableList>
  );
}
